import { defineRegistry } from "@json-render/react";
import { catalog } from "./catalog";
import { useNomniDispatch } from "./dispatch";

/**
 * React implementations of every component the catalog declares. The agent
 * and the pages both render through the SAME registry so any catalog change
 * propagates to both surfaces (generative UI parity).
 *
 * Per-click params (outletId, poId, ...) travel via a NomniDispatch React
 * context — NOT through json-render's typed-action system, which trades
 * ergonomics for type safety we don't need.
 */

function OutletPillImpl({ props }: { props: { outletId: string; outletName: string; status: string | null; timeZone: string | null; selected: boolean | null } }) {
  const dispatch = useNomniDispatch();
  return (
    <button
      onClick={() => dispatch("selectOutlet", { outletId: props.outletId })}
      className={`group flex items-center gap-3 w-full text-left rounded-[var(--radius-card)] bg-panel px-4 py-3 ring-1 transition hover:shadow-[var(--shadow-rise)] ${
        props.selected ? "ring-nomni bg-mint/40" : "ring-rule"
      }`}
    >
      <div className="size-9 rounded-lg bg-nomni/15 text-nomni-deep grid place-items-center font-medium">
        {props.outletName.slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-ink font-medium truncate">{props.outletName}</div>
        <div className="text-xs text-ink-mute">
          {props.outletId}
          {props.timeZone ? ` · ${props.timeZone}` : ""}
        </div>
      </div>
      {props.status ? (
        <span className="text-[10px] uppercase tracking-wider rounded-full bg-placed-soft text-nomni-deep px-2 py-0.5">{props.status}</span>
      ) : null}
    </button>
  );
}

function SupplierRowImpl({ props }: { props: { supplierId: string; supplierName: string; deliveryDays: string | null; minOrder: string | null } }) {
  const dispatch = useNomniDispatch();
  return (
    <button
      onClick={() => dispatch("openSupplier", { supplierId: props.supplierId })}
      className="w-full flex items-center justify-between rounded-[var(--radius-card)] bg-panel px-4 py-3 ring-1 ring-rule hover:ring-nomni transition"
    >
      <div className="text-left">
        <div className="text-ink font-medium">{props.supplierName}</div>
        <div className="text-xs text-ink-mute">{props.supplierId}</div>
      </div>
      <div className="text-xs text-ink-mute text-right">
        {props.deliveryDays ? <div>delivers: {props.deliveryDays}</div> : null}
        {props.minOrder ? <div>min: {props.minOrder}</div> : null}
      </div>
    </button>
  );
}

function OrderRowImpl({ props }: { props: { poId: string; supplierName: string; total: string; status: string; placedAt: string } }) {
  const dispatch = useNomniDispatch();
  const toneClass: Record<string, string> = {
    placed: "bg-placed-soft text-nomni-deep",
    pending: "bg-pending-soft text-[oklch(0.42_0.10_85)]",
    acknowledged: "bg-mint text-nomni-deep",
    received: "bg-nomni/15 text-nomni-deep",
    cancelled: "bg-danger-soft text-danger",
  };
  return (
    <button
      onClick={() => dispatch("openOrder", { poId: props.poId })}
      className="w-full grid grid-cols-[1fr_auto_auto] items-center gap-4 rounded-[var(--radius-card)] bg-panel px-4 py-3 ring-1 ring-rule hover:ring-nomni transition text-left"
    >
      <div>
        <div className="text-ink font-medium">{props.supplierName}</div>
        <div className="text-xs text-ink-mute">PO {props.poId} · {props.placedAt}</div>
      </div>
      <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${toneClass[props.status] ?? ""}`}>
        {props.status}
      </span>
      <div className="text-ink tabular-nums">{props.total}</div>
    </button>
  );
}

function ButtonImpl({ props }: { props: { label: string; action: string; variant: string | null; size: string | null } }) {
  const dispatch = useNomniDispatch();
  const v = props.variant ?? "primary";
  const s = props.size ?? "md";
  const base = "rounded-full font-medium transition active:scale-[0.98]";
  const variantClass =
    v === "ghost" ? "bg-transparent text-ink-2 hover:bg-rule/60" :
    v === "danger" ? "bg-danger text-white hover:brightness-105" :
    "bg-nomni-deep text-white hover:bg-nomni-leaf";
  const sizeClass = s === "sm" ? "px-3 py-1.5 text-xs" : s === "lg" ? "px-5 py-3 text-base" : "px-4 py-2 text-sm";
  return (
    <button onClick={() => dispatch(props.action)} className={`${base} ${variantClass} ${sizeClass}`}>
      {props.label}
    </button>
  );
}

export const { registry } = defineRegistry(catalog, {
  components: {
    Page: ({ props, children }) => (
      <div className="px-8 py-6 max-w-6xl mx-auto rise">
        <header className="mb-6">
          <h1 className="text-3xl font-medium tracking-tight text-ink">{props.title}</h1>
          {props.subtitle ? (
            <p className="text-ink-mute mt-1 text-balance">{props.subtitle}</p>
          ) : null}
        </header>
        <div className="space-y-6">{children}</div>
      </div>
    ),
    Section: ({ props, children }) => {
      const gap = props.density === "tight" ? "space-y-2" : props.density === "airy" ? "space-y-6" : "space-y-3";
      return (
        <section>
          {props.title ? (
            <h2 className="text-xs uppercase tracking-[0.18em] text-ink-mute mb-3">{props.title}</h2>
          ) : null}
          <div className={gap}>{children}</div>
        </section>
      );
    },
    Card: ({ props, children }) => {
      const tone = props.tone ?? "plain";
      const ring =
        tone === "nomni" ? "ring-1 ring-nomni/30 bg-mint/30" :
        tone === "danger" ? "ring-1 ring-danger/40 bg-danger-soft/40" :
        tone === "amber" ? "ring-1 ring-amber/40 bg-pending-soft/50" :
        "ring-1 ring-rule";
      return (
        <div className={`rounded-[var(--radius-card)] bg-panel p-5 shadow-[var(--shadow-panel)] ${ring}`}>
          {props.eyebrow ? (
            <div className="text-[11px] uppercase tracking-[0.18em] text-ink-mute mb-1">{props.eyebrow}</div>
          ) : null}
          {props.title ? (
            <div className="text-lg text-ink font-medium mb-2">{props.title}</div>
          ) : null}
          {children}
        </div>
      );
    },
    KPI: ({ props }) => {
      const t = props.tone === "up" ? "text-nomni-leaf" : props.tone === "down" ? "text-danger" : "text-ink-mute";
      return (
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-ink-mute">{props.label}</div>
          <div className="text-3xl tabular-nums tracking-tight text-ink mt-1">{props.value}</div>
          {props.delta ? <div className={`text-xs mt-1 ${t}`}>{props.delta}</div> : null}
        </div>
      );
    },
    OutletPill: OutletPillImpl,
    SupplierRow: SupplierRowImpl,
    OrderRow: OrderRowImpl,
    Toolbar: ({ props, children }) => {
      const j = props.align === "between" ? "justify-between" : props.align === "end" ? "justify-end" : "justify-start";
      return <div className={`flex items-center gap-2 ${j}`}>{children}</div>;
    },
    Button: ButtonImpl,
    Markdown: ({ props }) => (
      <div className="prose prose-sm max-w-none text-ink-2 whitespace-pre-wrap leading-relaxed">{props.body}</div>
    ),
    EmptyState: ({ props }) => (
      <div className="text-center py-12">
        <div className="text-ink font-medium">{props.title}</div>
        {props.body ? <div className="text-ink-mute text-sm mt-1 text-balance">{props.body}</div> : null}
      </div>
    ),
    Skeleton: ({ props }) => (
      <div className="space-y-2">
        {Array.from({ length: props.lines ?? 3 }).map((_, i) => (
          <div key={i} className="h-3 rounded shimmer bg-rule" />
        ))}
      </div>
    ),
  },
});
